import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { getCheckoutSettings, updateCheckoutSettings } from './checkout-settings.api'
import type { CheckoutSettings } from './checkout-settings.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

export const checkoutSettingsQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'checkout-settings' }, { organizationId }),
  ],
  detail: ({ organizationId }: OrganizationScope = {}) =>
    buildScopedAdminQueryKey(checkoutSettingsQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'detail',
    }),
}

export const checkoutSettingsQueries = {
  detail: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: checkoutSettingsQueryKeys.detail({ organizationId }),
      queryFn: getCheckoutSettings,
    }),
}

export function useCheckoutSettings({ organizationId }: OrganizationScope = {}) {
  return useSuspenseQuery(
    checkoutSettingsQueries.detail({ organizationId: useRouteOrganizationId(organizationId) })
  )
}

export function useUpdateCheckoutSettingsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CheckoutSettings) => updateCheckoutSettings({ organizationId, ...values }),
    onSuccess: (data) => {
      queryClient.setQueryData(checkoutSettingsQueryKeys.detail({ organizationId }), data)
    },
  })
}
